import { FlatListActions, FlatListStatus } from '@/components'
import { GlobalConfigStoreType } from '@/hooks/useConfig'
import { IRestfulApiService, RestfulApiService } from '@/lib/apiService'
import { gaViewItemList, IgaViewItemList } from '@/lib/ga4'
import { mixpanelEvent } from '@/lib/mixpanels/mixpanel'
import { SearchFilterProperty } from '@/lib/mixpanels/mixpanelType'
import {
	deleteParamWithRouterEvent,
	Page,
	PageType,
	useUpdateRouterHistory,
} from '@/lib/pagination'
import { getUrlStringToParams } from '@/lib/utils/url'
import { IViewModel, ViewModelAction, ViewModelStatus } from '@/lib/viewModels'
import { ParseState, SortState } from '@/modules/products/components/ProductTypeSearchResultHeader'
import { LOGIN_PAGE, SHOP_CART_PAGE } from '@/settings/constant'
import { GetProductsQueryDTO } from '@bookips/solvook-api-shared'
import { nanoid } from '@solvook/nanoid'
import { Modal } from 'antd'
import { debounce } from 'debounce'
import { runInAction, toJS } from 'mobx'
import { useLocalObservable } from 'mobx-react-lite'
import { NextRouter } from 'next/router'
import { useEffect, useMemo } from 'react'
import { toQueryString } from 'src/utils/misc'
import { AddItemToCartCommand } from '../commands/AddItemToCartCommand'
import { GetFiltersCommand } from '../commands/getFiltersCommand'
import { GetProductsCommand } from '../commands/getProductsCommand'
import {
	ProductListItemData,
	ProductListItemMetadata,
	ProductListItemOnPriceChange,
} from '../components/ProductList'
import { Product, ProductStoreType, ProductVariantOption } from '../stores/ProductModel'

type ProductListViewModelProps = {
	useNewPlan?: boolean
	useParsingBadge?: boolean
	apiService?: IRestfulApiService
	router?: NextRouter
	config?: GlobalConfigStoreType
	rootStore?: ProductStoreType
	products?: Product[]
}

export interface FilterItemType {
	id: string
	label?: string
	value: string
	key?: string
	selected?: boolean
}

export interface FilterListType {
	handout?: HandoutFilterListType
	textbook?: LicenseFilterListType
	workbook?: LicenseFilterListType
}

export interface LicenseFilterListType {
	publisher?: string[]
	grade?: string[]
	subject?: string[]
}

export interface HandoutFilterListType {
	based_textbook?: string[]
	based_workbook?: string[]
	unit?: string[]
	handout_category?: string[]
	handout_author?: string[]
	grade?: string[]
	subject?: string[] | { label: string; value: string }[]
}

export type FilterMap = {
	[key in string]: FilterItemType[]
}

type ProductListViewModelStatus = ViewModelStatus &
	FlatListStatus & {
		showModal: boolean
		showFilter: boolean
		deleteFilterItem: any
		allChecked: boolean
		isModalOpen: boolean
		resetFilter: boolean
		currentSelectedInBox: FilterItemType[]
		currentModalIdx: number
		sort: SortState
		parsing: ParseState
		useNewPlan: boolean
		useParsingBadge: boolean
	}

type SearchState = 'searching' | 'searched' | 'empty'
type SearchType = 'handout' | 'textbook' | 'workbook'
type SearchTypeMap = {
	[key in SearchType]: ProductListItemData[]
}

type ProductListViewModelActions = ViewModelAction &
	FlatListActions<ProductListItemData, ProductListItemMetadata> & {
		getPagination: () => PageType
		onEmptyClick: () => void
		onResetFilter: () => void
		onPriceChange: ProductListItemOnPriceChange
		updatePageChange: (pageIndex: number) => void
		deleteFilterItem: (item: FilterItemType) => void
		onChangeFilterItems: (filter: string, items: FilterItemType[]) => void
		setKeyword: (keyword: string) => void
		onCart: () => Promise<void>
		onSorting: (sort: SortState) => void
		onParsing: (parsing: ParseState) => void
		onShowModal: (item: string[], idx: number) => void
		onShowMobileFilter: () => void
		fetchProducts: (dto?: GetProductsQueryDTO, fetchMore?: boolean) => Promise<void>
		fetchFilters: (category: string) => Promise<FilterListType>
		onSelectAll: (checked: boolean) => void
		onChangeSelectedItems: (checked: boolean, item: ProductListItemData) => void
		goToCart: () => void
		invalidateQueryFilterFromRouter: (query: object) => Promise<FilterMap>
		get selected(): ProductListItemData
		get keyword(): string
		get searchState(): SearchState
		get filters(): FilterListType
		get category(): string
		get currentSelectedFilters(): FilterMap
		get currentFilters(): LicenseFilterListType | HandoutFilterListType
		get selectedItems(): ProductListItemData[]
	}

interface ProductListViewLocalStore {
	_items: ProductListItemData[]
	_selectedFilters: FilterMap
	selected: ProductListItemData
	paging: PageType
	_keyword: string
	_filters: FilterListType
	_category: string
	_selectedItems: ProductListItemData[]
}

type IProductListViewModel = IViewModel<
	ProductListViewModelProps,
	ProductListViewModelStatus,
	ProductListViewModelActions
>

const toProductListItemFromProduct = (product: Product): ProductListItemData => {
	return {
		id: product.id,
		title: product.title,
		product,
	} as ProductListItemData
}

const allowedFilterKey = [
	'q',
	'author',
	'publisher',
	'subject',
	'curriculum',
	'grade',
	'based_textbook',
	'based_workbook',
	'unit',
	'handout_category',
]

const isV1Url = (query: object): boolean => {
	// https://www.solvook.com/market?type=resource&subject=EN&publisher=&grade=20&originType=%EB%B6%80%EA%B5%90%EC%9E%AC&author=%EC%9D%B4%ED%81%B4%EB%9D%BC%EC%9A%B0%EB%93%9C&series=%EC%88%98%EB%8A%A5%EB%A7%8C%EB%A7%8C%20%EC%8B%9C%EB%A6%AC%EC%A6%88%20%5B%EB%8A%A5%EB%A5%A0%5D&origin=23389&subtype=%EC%9B%8C%ED%81%AC%EB%B6%81&unit=&sort=name&order=descend&keyword=%EC%88%98%EB%8A%A5%EB%A7%8C%EB%A7%8C%20%EA%B8%B0%EB%B3%B8
	// type=resource&subject=EN&publisher=&grade

	const v1Filters = ['subtype', 'order', 'type', 'originType', 'origin', 'series', 'keyword']
	return Object.keys(query).some((key) => v1Filters.includes(key))
}

const convertV1UrlToV2 = (query: object): object => {
	const isV1 = isV1Url(query)
	const keys = Object.keys(query)
	if (isV1) {
		// @ts-ignore
		const { keyword, author, ...rest } = query

		let nextQuery: object = {}

		if (author && author !== '') {
			nextQuery = {
				...nextQuery,
				author,
			}
		}

		if (keyword && keyword !== '') {
			nextQuery = {
				...nextQuery,
				q: keyword,
			}
		}

		return nextQuery
	} else {
		if (keys.includes('grade')) {
			delete query['grade']
		}

		if (keys.includes('sort')) {
			if (query['sort'] === 'updated') {
				delete query['sort']
			}
		}

		return Object.entries(query)
			.filter(([key, value]) => {
				if (typeof value === 'string') {
					return value !== ''
				} else {
					return true
				}
			})
			.reduce((acc, [key, value]) => {
				acc[key] = value
				return acc
			}, {})
	}
}

export const ProductListViewModel: IProductListViewModel = ({
	apiService,
	config,
	router,
	rootStore,
	products,
	useNewPlan,
	useParsingBadge,
}) => {
	const q = (router.query.q as string) || (router.query.keyword as string) || ''
	const defaultPageLimit = config?.defaultPageLimit || 10

	let routerHistory = useUpdateRouterHistory({ router, defaultPageLimit })

	const getProductListCommand = useMemo(
		() => new GetProductsCommand(apiService as RestfulApiService, rootStore),
		[apiService, rootStore],
	)
	const getFilterCommand = useMemo(
		() => new GetFiltersCommand(apiService as RestfulApiService),
		[apiService, rootStore],
	)

	const addItemToCartCommand = useMemo(() => new AddItemToCartCommand(apiService), [apiService])

	useEffect(() => {
		const updateCategoryByUrl = async (path: string, { shallow }) => {
			if (shallow) return

			const getUrlToSlug = (path: string): string => {
				const url = path.split('?')[0]
				const slug = url.split('/').pop()
				return slug
			}
			const category = getUrlToSlug(path)
			const urlStringToParams = getUrlStringToParams(path)

			if (
				toJS(rootStore.products.pagination._offset) >= 0 &&
				urlStringToParams?.offset &&
				parseInt(urlStringToParams?.offset) !== toJS(rootStore.products.pagination._offset)
			) {
				console.log(1)
				await actions.fetchProducts({
					offset: urlStringToParams?.offset,
				})
			}
			if (category !== actions._category) {
				actions._category = category
				actions._selectedFilters = {}
				await actions.fetchProducts({
					category,
				})
			}
		}

		router.events.on('routeChangeComplete', updateCategoryByUrl)
		return () => {
			router.events.off('routeChangeComplete', updateCategoryByUrl)
		}
	}, [router])

	const actionFilterToQueryString = (filters): string => {
		const filterQuery = Object.entries(filters).reduce((acc, [key, value]) => {
			if (Array.isArray(value) && value.length > 0) {
				acc[key] = value.map((item) => item.value).join(',')
			}
			return acc
		}, {})
		return toQueryString(filterQuery, false)
	}

	const status = useLocalObservable<ProductListViewModelStatus>(() => ({
		addingItems: false,
		errors: [],
		initialized: false,
		loading: false,
		refreshing: false,
		showModal: false,
		showFilter: false,
		filterItems: [],
		deleteFilterItem: '',
		allChecked: false,
		isModalOpen: false,
		resetFilter: false,
		currentSelectedInBox: [],
		currentModalIdx: 0,
		sort: 'new',
		parsing: router.query.parsing === 'Y' ? 'Y' : 'N',
		useNewPlan,
		useParsingBadge,
	}))

	const actions = useLocalObservable<ProductListViewModelActions & ProductListViewLocalStore>(
		() => ({
			_keyword: q || '',
			_items: [],
			_selectedItems: [],
			paging: Page({ pageSize: defaultPageLimit }),
			_filters: {},
			_selectedFilters: {},
			_category: (router.query?.slug as string) || '',
			async invalidateQueryFilterFromRouter(query) {
				const filterQuery = Object.keys(query)
				if (filterQuery.length > 0) {
					const validatedFilter = filterQuery
						.filter((key) => allowedFilterKey.includes(key))
						.sort()

					const newSelectedFilters = validatedFilter.reduce((acc, cur, idx) => {
						const currentQuery = query[cur]
						if (typeof currentQuery === 'string' && query[cur]?.includes(',')) {
							// query 가 배열인 경우
							acc[cur] = (query[cur] as string)
								.split(',')
								.filter((item) => item && item !== '')
								.map((filter) => {
									return {
										id: nanoid(),
										key: cur,
										value: filter,
									} as FilterItemType
								})
						} else {
							if (query[cur] && query[cur] !== '') {
								const convertableKeys = [
									'handout_category',
									'curriculum',
									'subject',
								]

								if (
									actions.currentFilters &&
									actions.currentFilters[cur] &&
									convertableKeys.includes(cur)
								) {
									const currentFilter = actions.currentFilters[cur]
									let filterItem

									if (currentFilter && currentFilter.length > 0) {
										if (typeof currentFilter[0] === 'string') {
											filterItem = currentFilter.find(
												(item) => item === query[cur],
											)
										} else {
											filterItem = currentFilter.find(
												(item) => item.value === query[cur],
											)
										}
									}

									if (filterItem) {
										acc[cur] = [
											{
												id: nanoid(),
												...filterItem,
												key: cur,
											} as FilterItemType,
										]
									} else {
										acc[cur] = [
											{
												id: nanoid(),
												key: cur,
												value: query[cur],
											} as FilterItemType,
										]
									}
								} else {
									acc[cur] = [
										{
											id: nanoid(),
											key: cur,
											value: query[cur],
										} as FilterItemType,
									]
								}
							}
						}

						return acc
					}, {})

					return Promise.resolve(newSelectedFilters)
				} else {
					return Promise.resolve({})
				}
			},

			async initialize(props: {}) {
				const category = (router.query.slug as string) || 'handout'
				const q = (router.query.q as string) || (router.query.keyword as string) || ''
				actions._category = category
				const offset = Number(router.query.offset as string)
				const limit = Number(router.query.limit as string)

				await actions.fetchFilters(category)

				actions._selectedFilters = await actions.invalidateQueryFilterFromRouter(
					router.query,
				)

				if (offset) {
					await actions.fetchProducts({
						q,
						category,
						offset,
						limit: limit ?? defaultPageLimit,
					})
				} else {
					await actions.fetchProducts({ q, category, offset: 0, limit: defaultPageLimit })
				}

				Object.entries(router.query).map(([key, value]) => {
					routerHistory.updateFilterSearch(key, value)
				})

				if (rootStore?.products.pagination) {
					routerHistory.updatePagination(
						rootStore.products.pagination.offset,
						rootStore.products.pagination.limit,
					)
				}

				status.currentSelectedInBox = []
				status.currentModalIdx = 0
				const items: IgaViewItemList[] = [
					{
						item_id: actions.items[0]?.product?.id,
						price: actions.items[0]?.product?.price,
						item_category: actions.items[0]?.product.source_type,
					},
				]

				gaViewItemList({
					item_list_id: actions.category,
					item_list_name: actions.category,
					items,
				})

				status.initialized = true
				status.loading = false
			},
			dispose(): void {},

			async fetchProducts(
				dto: GetProductsQueryDTO = {
					offset: 0,
					limit: defaultPageLimit,
				},
				fetchMore: boolean = false,
			) {
				const newParams: Partial<GetProductsQueryDTO> = {
					q: this.keyword,
					offset: 0,
					limit: defaultPageLimit,
					category: this.category,
					sort: status.sort,
					parsing: status.parsing,
					...dto,
				}
				Object.entries(actions._selectedFilters).map(([key, value]) => {
					if (allowedFilterKey.includes(key)) {
						newParams[key] = value.map((item) => item.value)
					}
				})

				try {
					runInAction(() => {
						status.loading = true
					})

					const response = await getProductListCommand.execute(newParams)

					// 딜레이 디버그용
					// await delay(1000)

					runInAction(() => {
						status.loading = false
					})

					// actions.paging._total = response.products.pagination.total
				} catch (e) {
					Modal.error({
						title: 'Error',
						content: e.message,
					})
				}
			},

			async fetchFilters(slug: string) {
				try {
					const response = await getFilterCommand.execute({})
					if (response.success) {
						runInAction(() => {
							actions._filters = { ...response.filter } as FilterListType
						})

						return response.filter
					}
				} catch (e) {
					Modal.error({
						title: 'Error',
						content: e.message,
					})
				}
			},
			async onPriceChange(product: Product, variant: ProductVariantOption) {},

			async onResetFilter() {
				actions._selectedFilters = {}
				status.resetFilter = true

				await actions.fetchProducts({ offset: 0, limit: defaultPageLimit })

				this.invalidateFilterQuery(actions._selectedFilters)

				if (
					(router.query.sort && status.sort) ||
					(router.query.parsing && status.parsing)
				) {
					if (router.query.sort && status.sort)
						routerHistory.updateFilterSearch('sort', status.sort)
					if (router.query.parsing && status.parsing)
						routerHistory.updateFilterSearch('parsing', status.parsing)
					routerHistory.invalidate()
				}

				status.resetFilter = false
			},

			async onSorting(sort) {
				status.sort = sort
				await actions.fetchProducts({
					offset: 0,
					limit: defaultPageLimit,
				})

				routerHistory.updateFilterSearch('sort', status.sort)
			},
			async onParsing(parsing) {
				status.parsing = parsing
				await actions.fetchProducts({
					offset: 0,
					limit: defaultPageLimit,
				})

				routerHistory.updatePagination(0)
				routerHistory.updateFilterSearch('parsing', status.parsing)
				routerHistory.invalidate()
				setTimeout(() => {
					if (parsing === 'Y') {
						mixpanelEvent('Search Filtered', {
							'Filter Location': 'legacy',
							'Filter Category': '편집가능',
							'Filter Option Selected': 'Y',
							'# of Results': actions.getPagination()._total,
						} as SearchFilterProperty)
					}
				}, 1000)
			},
			get selectedItems(): ProductListItemData[] {
				return actions._selectedItems
			},
			get items(): ProductListItemData[] {
				return (
					products?.map(toProductListItemFromProduct) ||
					rootStore?.products?.products.map(toProductListItemFromProduct) ||
					[]
				)
			},
			get filters(): FilterListType {
				return actions._filters
			},

			get currentFilters(): LicenseFilterListType | HandoutFilterListType {
				return actions.filters[actions.category]
			},
			get currentSelectedFilters(): FilterMap {
				return actions._selectedFilters
			},
			get searchState(): SearchState {
				return undefined
			},
			get category(): string {
				return actions._category
			},

			getPagination(): PageType {
				return rootStore.products.pagination ?? actions.paging
			},

			async updatePageChange(pageIndex: number) {
				const { offset, limit } = rootStore.products?.pagination.next(pageIndex)
				// await actions.fetchProducts({ offset, limit })

				actions.paging._offset = offset
				routerHistory.updatePagination(offset, limit)
				const filter = actionFilterToQueryString(toJS(actions._selectedFilters))
				const pageInfo = toQueryString({ offset, limit }, false)
				router.push(
					`/category/${actions.category}?${filter ? `${filter}&` : ''}${pageInfo}`,
				)
			},
			setKeyword(keyword: string): void {
				actions._keyword = keyword
			},

			get keyword(): string {
				return actions._keyword
			},
			//체크박스 선택한 애들 배열에 담기
			get selected(): ProductListItemData {
				return actions.selected
			},

			async onItemClick({ item, metadata }) {
				alert(JSON.stringify(item))
			},
			invalidateFilterQuery(newValue: FilterMap) {
				routerHistory.clearAllFilterSearch()

				Object.entries(newValue).map(([key, value]) => {
					routerHistory.updateFilterSearch(
						key,
						value.map((item) => item.value),
					)
				})

				routerHistory.updateFilterSearch('slug', actions.category)
				routerHistory.invalidate()
			},

			async onChangeFilterItems(filter, items) {
				//mixpanel
				let addFilter = []
				let removeFilter = []
				let searchStatus = false
				let itemsArr = items?.map((item) => item.value)
				let selectedFiltersArr = actions._selectedFilters[filter]?.map((item) => item.value)
				if (!selectedFiltersArr) {
					searchStatus = true
					addFilter = itemsArr
				} else if (actions._selectedFilters[filter]?.length > items.length) {
					//삭제
					removeFilter = selectedFiltersArr?.filter((x) => !itemsArr.includes(x))
					searchStatus = false
				} else if (actions._selectedFilters[filter]?.length < items.length) {
					//추가
					addFilter = itemsArr.filter((x) => !selectedFiltersArr.includes(x))
					searchStatus = true
				}
				runInAction(() => {
					actions._selectedFilters[filter] = items

					if (items.length === 0) {
						delete actions._selectedFilters[filter]
					}
				})

				await actions.fetchProducts({})

				routerHistory.updatePagination(0)

				if (items.length > 0) {
					routerHistory.updateFilterSearch(
						filter,
						items.map((item) => item.value),
					)

					router.replace(
						`/category/${actions.category}?${actionFilterToQueryString(
							toJS(actions._selectedFilters),
						)}`,
					)
					// routerHistory.invalidate(undefined, true)
				} else {
					routerHistory.deleteFilterSearch(filter)
					deleteParamWithRouterEvent(filter, router)
				}

				//mixpanel
				setTimeout(() => {
					if (searchStatus) {
						mixpanelEvent('Search Filtered', {
							'Filter Location': 'legacy',
							'Filter Category': filter,
							'Filter Option Selected': addFilter[0],
							'# of Results': actions.getPagination()._total,
						} as SearchFilterProperty)
					}
				}, 1000)
			},

			async deleteFilterItem(item: FilterItemType) {
				const key = item.key

				runInAction(() => {
					actions._selectedFilters[item.key] = actions._selectedFilters[key].filter(
						(sub) => sub.id !== item.id,
					)

					if (actions._selectedFilters[key].length === 0) {
						delete actions._selectedFilters[key]
					}
				})

				router.replace(
					`/category/${actions.category}?${actionFilterToQueryString(
						toJS(actions._selectedFilters),
					)}`,
				)

				await actions.fetchProducts({})
			},

			onEmptyClick(): void {},

			async onCart() {
				if (actions.selectedItems.length === 0) {
					Modal.error({
						title: 'Error',
						content: '장바구니에 담을 상품을 선택해주세요',
					})
					return
				}
				try {
					// 베리언트 아이디를 selected item에서 갖고 있는다.
					// 장바구니 버튼을 클릭하면 넣어준다. variant id 를 넣어준다.
					for (let i = 0; i < actions._selectedItems.length; i++) {
						const response = await addItemToCartCommand.execute(null, {
							product_id: actions._selectedItems[i].id,
							product_variant_id:
								actions._selectedItems[i].product.product_variants[0].id,
							quantity: 1,
						})
						status.isModalOpen = true
						setTimeout(() => (status.isModalOpen = false), 1500)
						if (response.success) {
						}
					}
				} catch (e) {
					const loginModal = () => {
						const modal = Modal.success({
							title: '로그인을 해주세요',
							content: '로그인페이지로 이동합니다.',
						})
						setTimeout(() => {
							modal.destroy()
						}, 3000)
					}
					loginModal()
					await router?.push(LOGIN_PAGE)
				} finally {
				}
			},

			onLoadMore: debounce(
				async () => {
					status.addingItems = true

					try {
					} catch (e) {}

					status.addingItems = false
				},
				300,
				true,
			),
			onShowModal(items, idx): void {
				status.showModal = !status.showModal
			},
			onShowMobileFilter(): void {
				status.showFilter = !status.showFilter
			},
			//전체선택
			onSelectAll(checked): void {
				status.allChecked = checked
				if (checked) {
					//모든 체크박스의 checked를 true로
				} else {
					actions._selectedItems = []

					//체크박스의 checked를 false로
				}
			},
			//체크박스 선택
			onChangeSelectedItems(checked, item): void {
				if (checked) {
					actions._selectedItems.push(item)
				} else if (!checked) {
					actions._selectedItems = actions._selectedItems.filter((selectedItem) => {
						return selectedItem.id !== item.id
					})
				}
			},
			//장바구니로 가기
			goToCart(): void {
				router?.push(SHOP_CART_PAGE)
			},
			onRefresh: debounce(
				async () => {
					status.refreshing = true
					try {
					} catch (e) {}

					status.refreshing = false
				},
				300,
				true,
			),
		}),
	)
	useEffect(() => {
		actions._keyword = router.query.q as string
	}, [router.query.q])

	useEffect(() => {
		actions.initialize({})
	}, [])
	useEffect(() => {
		mixpanelEvent('Product List Page Viewed', {
			'Filter Location': 'legacy',
			'Product Category': router.query.slug as string,
		})
	}, [router.query.slug])

	return useLocalObservable(() => ({
		status,
		actions,
	}))
}
export type ProductListViewModelType = ReturnType<typeof ProductListViewModel>
